import {
  Alert,
  Box,
  Button,
  Card,
  Center,
  Group,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconAlertTriangle, IconKey } from '@tabler/icons-react';
import { startAuthentication } from '@simplewebauthn/browser';
import type { PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../api/client';
import { humanizeWebAuthnError } from '../api/webauthn-errors';
import {
  useLogin,
  useLoginPasskeyOptions,
  useLoginPasskeyVerify,
  useLoginTotp,
} from '../api/session';

type Stage =
  | { kind: 'password' }
  | { kind: 'mfa'; token: string; factors: Array<'totp' | 'passkey'> };

export function LoginPage() {
  const navigate = useNavigate();
  const login = useLogin();
  const loginTotp = useLoginTotp();
  const passkeyOptions = useLoginPasskeyOptions();
  const passkeyVerify = useLoginPasskeyVerify();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [stage, setStage] = useState<Stage>({ kind: 'password' });

  function handlePasswordSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    login.mutate(
      { email, password },
      {
        onSuccess: (data) => {
          if (data.mfaToken && data.factors && data.factors.length > 0) {
            setStage({ kind: 'mfa', token: data.mfaToken, factors: data.factors });
          } else {
            navigate('/');
          }
        },
        onError: (err) => {
          if (err instanceof ApiError) {
            if (err.status === 401) setError('Invalid email or password.');
            else if (err.status === 403) {
              const parsed = (() => {
                try {
                  return JSON.parse(err.body) as { message?: string };
                } catch {
                  return null;
                }
              })();
              setError(
                parsed?.message?.startsWith('mfa_required_but_not_enrolled')
                  ? 'MFA is required for this account, but no factor is enrolled. Contact an administrator.'
                  : 'This account is disabled.',
              );
            } else setError('Sign in failed. Please try again.');
          } else {
            setError('Sign in failed. Please try again.');
          }
        },
      },
    );
  }

  function handleTotpSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (stage.kind !== 'mfa') return;
    setError(null);
    loginTotp.mutate(
      { mfaToken: stage.token, code: code.trim() },
      {
        onSuccess: () => navigate('/'),
        onError: (err) => {
          if (err instanceof ApiError && err.status === 401) setError('Invalid code.');
          else setError('Could not verify code. Try again.');
        },
      },
    );
  }

  async function handlePasskey() {
    if (stage.kind !== 'mfa') return;
    setError(null);
    try {
      const opts = (await passkeyOptions.mutateAsync({
        mfaToken: stage.token,
      })) as PublicKeyCredentialRequestOptionsJSON;
      const assertion = await startAuthentication({ optionsJSON: opts });
      await passkeyVerify.mutateAsync({ mfaToken: stage.token, response: assertion });
      navigate('/');
    } catch (err) {
      setError(humanizeWebAuthnError(err, 'authenticate'));
    }
  }

  return (
    <Center h="100vh">
      <Card shadow="md" padding="xl" maw={420} w="100%">
        {stage.kind === 'password' && (
          <Box component="form" onSubmit={handlePasswordSubmit}>
            <Stack gap="md">
              <Title order={3}>Sign in</Title>
              <Text c="dimmed" size="sm">
                Skin Analytics admin console — internal staff only.
              </Text>

              {error && (
                <Alert color="red" icon={<IconAlertTriangle size={18} />} role="alert">
                  {error}
                </Alert>
              )}

              <TextInput
                label="Email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                disabled={login.isPending}
              />
              <PasswordInput
                label="Password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                disabled={login.isPending}
              />
              <Button type="submit" loading={login.isPending} size="md">
                Sign in
              </Button>
            </Stack>
          </Box>
        )}

        {stage.kind === 'mfa' && (
          <Stack gap="md">
            <Title order={3}>Two-factor sign in</Title>
            <Text c="dimmed" size="sm">
              Your account is protected by MFA. Choose a factor to continue.
            </Text>

            {error && (
              <Alert color="red" icon={<IconAlertTriangle size={18} />} role="alert">
                {error}
              </Alert>
            )}

            {stage.factors.includes('passkey') && (
              <Button
                leftSection={<IconKey size={16} />}
                onClick={handlePasskey}
                loading={passkeyOptions.isPending || passkeyVerify.isPending}
                variant="filled"
              >
                Use a passkey / biometric
              </Button>
            )}

            {stage.factors.includes('totp') && (
              <Box component="form" onSubmit={handleTotpSubmit}>
                <Stack gap="sm">
                  <TextInput
                    label="Authenticator app code"
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    maxLength={6}
                    required
                    value={code}
                    onChange={(e) => setCode(e.currentTarget.value)}
                  />
                  <Group justify="space-between">
                    <Button
                      variant="default"
                      onClick={() => {
                        setStage({ kind: 'password' });
                        setCode('');
                        setError(null);
                      }}
                    >
                      Start over
                    </Button>
                    <Button type="submit" loading={loginTotp.isPending}>
                      Verify code
                    </Button>
                  </Group>
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </Card>
    </Center>
  );
}
